import { useCallback, useState } from 'react';

import { useQuery } from 'react-query';

import {
  downloadCsv,
  downloadOffersCsv,
  generateCsv,
  generateOffersCsv,
  getCsvStatus,
  getOffersCsvStatus,
} from '../../../services/operation';
import {
  downloadCsv as downloadCsvDebentures,
  generateCsv as generateCsvDebentures,
  getCsvStatus as getCsvStatusDebentures,
} from '../../../services/operation/operationDebentures';
import {
  downloadCsv as downloadCsvFiagro,
  generateCsv as generateCsvFiagro,
  getCsvStatus as getCsvStatusFiagro,
} from '../../../services/operation/operationFiagro';

import type { AxiosResponse } from 'axios';

const MARKETS = [
  'CR',
  'CRI',
  'CRA',
  'FII',
  'FIDC',
  'FIAGRO',
  'DEBENTURES',
] as const;
type Market = (typeof MARKETS)[number];
type MarketInput = Market | '';

type CsvExportParams = {
  market?: string;
  mode?: string;
  indicator?: string;
  permission?: string;
  filters?: Record<string, any>;
  sorting?: Record<string, any>;
};

type CsvResponse = {
  jobId: string;
  status: 'processing' | 'completed' | 'error';
  progress?: number;
  message: string;
  downloadUrl?: string;
  filename?: string;
};

type CsvHandlers = {
  generate: (params: CsvExportParams) => Promise<CsvResponse>;
  status: (jobId: string) => Promise<CsvResponse>;
  download: (jobId: string) => Promise<AxiosResponse<Blob>>;
};

const offerIndicators = [
  'cri-ofertas',
  'cra-ofertas',
  'fii-ofertas',
  'fidc-ofertas',
];

const defaultMarketHandlers: CsvHandlers = {
  generate: generateCsv,
  status: getCsvStatus,
  download: downloadCsv,
};

const offersHandlers: CsvHandlers = {
  generate: generateOffersCsv,
  status: getOffersCsvStatus,
  download: downloadOffersCsv,
};

const marketOverrides: Partial<Record<Market, CsvHandlers>> = {
  FIAGRO: {
    generate: generateCsvFiagro,
    status: getCsvStatusFiagro,
    download: downloadCsvFiagro,
  },
  DEBENTURES: {
    generate: generateCsvDebentures,
    status: getCsvStatusDebentures,
    download: downloadCsvDebentures,
  },
};

const marketMapFunctions: Record<Market, CsvHandlers> = {
  CR: defaultMarketHandlers,
  CRA: defaultMarketHandlers,
  CRI: defaultMarketHandlers,
  FIDC: defaultMarketHandlers,
  FII: defaultMarketHandlers,
  FIAGRO: marketOverrides.FIAGRO ?? defaultMarketHandlers,
  DEBENTURES: marketOverrides.DEBENTURES ?? defaultMarketHandlers,
};

const isMarket = (value: MarketInput): value is Market =>
  MARKETS.includes(value as Market);

const getFilenameFromContentDisposition = (contentDisposition?: unknown) => {
  const headerValue =
    typeof contentDisposition === 'string' ? contentDisposition : undefined;
  const filenameMatch = headerValue?.match(/filename="?([^";]+)"?/i);

  return filenameMatch?.[1] || 'export.csv';
};

const triggerBlobDownload = (blob: Blob, filename: string) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');

  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

interface CsvJob {
  jobId: string;
  status: 'processing' | 'completed' | 'error';
  progress: number;
  message: string;
  downloadUrl?: string;
  filename?: string;
}

export const useCsvExport = (market: MarketInput, indicator?: string) => {
  const [currentJob, setCurrentJob] = useState<CsvJob | null>(null);
  const [isExporting, setIsExporting] = useState(false);
  const currentMarketHandlers = offerIndicators.includes(indicator ?? '')
    ? offersHandlers
    : isMarket(market)
      ? marketMapFunctions[market]
      : null;

  const { isFetching, isLoading } = useQuery(
    ['csvStatus', market, indicator, currentJob?.jobId],
    async () => {
      if (!currentMarketHandlers || !currentJob?.jobId) {
        throw new Error('Não foi possível consultar o status da exportação.');
      }

      return currentMarketHandlers.status(currentJob.jobId);
    },
    {
      enabled:
        !!currentMarketHandlers &&
        !!currentJob?.jobId &&
        currentJob?.status === 'processing',
      refetchInterval:
        currentJob?.progress && currentJob.progress < 100 ? 2000 : false, // 2 seconds
      refetchIntervalInBackground: true,
      onSuccess: (data) => {
        setCurrentJob((prev) =>
          prev
            ? {
                ...prev,
                status: data.status,
                progress: data.progress || 0,
                message: data.message,
                downloadUrl: data.downloadUrl,
                filename: data.filename,
              }
            : null,
        );

        if (data.status === 'completed' || data.status === 'error') {
          setIsExporting(false);
        }
      },
      onError: () => {
        setCurrentJob((prev) =>
          prev
            ? {
                ...prev,
                status: 'error',
                message: 'Erro ao verificar status da exportação',
              }
            : null,
        );
        setIsExporting(false);
      },
    },
  );

  const startCsvExport = useCallback(
    async (params: CsvExportParams) => {
      if (!currentMarketHandlers) {
        throw new Error('Selecione um mercado válido para exportar.');
      }

      try {
        setIsExporting(true);
        setCurrentJob(null);

        const response = await currentMarketHandlers.generate(params);

        setCurrentJob({
          jobId: response.jobId,
          status: response.status,
          progress: response?.progress || 0,
          message: response.message,
          filename: response?.filename,
          downloadUrl: response?.downloadUrl,
        });

        if (response.status === 'completed' && response.downloadUrl) {
          setIsExporting(false);
        }

        return response;
      } catch (error) {
        setIsExporting(false);
        setCurrentJob({
          jobId: '',
          status: 'error',
          progress: 0,
          message: 'Erro ao iniciar exportação',
        });
        throw error;
      }
    },
    [currentMarketHandlers],
  );

  const resetExport = useCallback(() => {
    setCurrentJob(null);
    setIsExporting(false);
  }, []);

  const downloadCurrentCsv = useCallback(async () => {
    if (!currentMarketHandlers || !currentJob?.jobId) {
      throw new Error('Não foi possível baixar o CSV gerado.');
    }

    const response = await currentMarketHandlers.download(currentJob.jobId);
    const filename = getFilenameFromContentDisposition(
      response.headers?.['content-disposition'],
    );

    triggerBlobDownload(response.data, filename);
  }, [currentJob?.jobId, currentMarketHandlers]);

  const getDownloadUrl = useCallback(() => {
    if (currentJob?.downloadUrl) {
      const baseUrl = process.env.REACT_APP_STRAPI_URL || '';
      return `${baseUrl}${currentJob.downloadUrl}`;
    }
    return '';
  }, [currentJob?.downloadUrl]);

  return {
    currentJob,
    isExporting,
    isPolling: isFetching || isLoading,
    resetExport,
    downloadCurrentCsv,
    startCsvExport,
    progress: currentJob?.progress || 0,
    downloadUrl: getDownloadUrl(),
    status: currentJob?.status,
    message: currentJob?.message || '',
  };
};
